import type { CustomFile } from '../types';

import type { TypeUploadConfig, TypeUploadResultPromise } from './types';
import sendBatchFiles from './sendBatchFiles';
import sendSequentialFiles from './sendSequentialFiles';
import sendUpload from './sendUpload';

// ----------------------------------------------------------------------------

export type TypeMode = 'batch' | 'limited' | 'parallel' | 'sequential';

// ----------------------------------------------------------------------------

/*
 * Dispatches to an entry point by mode.
 *
 * Three of the four modes are now the same call: parallel and limited are `sendUpload` with the
 * cap the config resolves to, and sequential is the same at `max: 1`. The names are kept because
 * callers pass a mode string, but new code can reach for `sendUpload` and set `concurrency`
 * directly. Batch stays its own entry point — one request for every file is a different shape.
 *
 * `sendSequentialGroups` is deliberately absent: it takes groups rather than a flat file list,
 * so it cannot share this signature.
 */
function sendFiles(mode: TypeMode, files: CustomFile[], config: TypeUploadConfig): TypeUploadResultPromise {
    if (mode === 'batch') {
        return sendBatchFiles(files, config);
    }

    if (mode === 'sequential') {
        return sendSequentialFiles(files, config);
    }

    if (mode === 'parallel' || mode === 'limited') {
        return sendUpload(files, config);
    }

    throw new Error(`Invalid upload mode: ${String(mode)}`);
}

// ----------------------------------------------------------------------------

export default sendFiles;
